// The role rule for the UI (ZENITH.md §6.2), so a page does not render an
// action the caller's role cannot take. The server is the authority; this only
// decides what to show.
//
// The capability table itself is not written here. It is generated from
// internal/authz into authz.gen.ts, because a hand-maintained copy fell four
// work packages behind the server's once already (#96). Only the walk lives in
// this file, and it mirrors Principal.RoleAt's three levels.
//
// **A role held at a parent customer reaches its children** (ADR-0315), and
// this learns which from `/v1/me`: each membership carries `beneath`, the ids
// the server's own walk reads for that grant (#1596). This file matches a row
// against them and never follows a parent itself — that rule has one
// implementation, on the server (ZENITH.md §6.2). An organization's and a
// site's `beneath` are always empty and are not read, as RoleAt does not read
// them: a site does not nest (ADR-0490).
import type { components } from "./api/schema";
import { CAPABILITIES, type Capability } from "./authz.gen";
import { listed } from "./api/openEnum";

type Me = components["schemas"]["Me"];
type Role = components["schemas"]["Role"];

export type { Capability };

// The two narrow roles rank after viewer, as on the server (ADR-0481 decision
// 8a); the server refuses one beside a different role over the same rows, so
// the order decides nothing a caller can hold.
const RANK: Record<Role, number> = { owner: 0, admin: 1, operator: 2, viewer: 3, guest_desk: 4, unit_manager: 5 };

/** The tenancy position of a row: its org, and customer and site when it has them.
 *
 * Deliberately not the shape of a resource. A Customer carries `id`, not
 * `customer_id`, so passing one here matched no customer membership and hid
 * from a customer's own admin what the server allows them (#180). `id` is
 * refused so a resource cannot be handed over in place of its position;
 * spell the row out: `{ org_id: c.org_id, customer_id: c.id }`. */
export interface RowScope { org_id?: string; customer_id?: string; site_id?: string; id?: never }

type MeMembership = Me["memberships"][number];

/** Whether a grant is held at the row `id` names or reaches it beneath its own
 * scope — Membership.holds on the server. */
function holds(m: MeMembership, id: string | undefined): boolean {
  return !!id && (m.scope_id === id || m.beneath.includes(id));
}

/** Best role the caller holds over a row, walking its ancestor chain. */
export function roleAt(me: Me, row: RowScope): Role | undefined {
  let best: Role | undefined;
  for (const m of me.memberships) {
    // A role this build does not know grants nothing here: the server decides what it grants, and
    // this page only hides what it cannot show (ADR-0511 5a).
    if (!listed(RANK, m.role)) continue;
    const hit =
      // The grant's own organization and no other: `beneath` is not read.
      (m.scope_type === "org" && m.scope_id === row.org_id) ||
      (m.scope_type === "customer" && holds(m, row.customer_id)) ||
      // The grant's own site and no other: a site does not nest (ADR-0490).
      (m.scope_type === "site" && m.scope_id === row.site_id);
    if (hit && (best === undefined || RANK[m.role] < RANK[best])) best = m.role;
  }
  return best;
}

export function can(me: Me | undefined, capability: Capability, row: RowScope): boolean {
  if (!me) return false;
  if (me.user.superuser) return true;
  if (capability === "org.create_root") return false;
  const role = roleAt(me, row);
  return role !== undefined && CAPABILITIES[role].has(capability);
}

/** Whether the caller holds `capability` anywhere at all; decides whether a
 * page or a "New" control is worth rendering before any row is known. */
export function canAnywhere(me: Me | undefined, capability: Capability): boolean {
  if (!me) return false;
  if (me.user.superuser) return true;
  return me.memberships.some((m) => listed(CAPABILITIES, m.role) && CAPABILITIES[m.role].has(capability));
}

/** The roles the caller may grant, invite a person to, withdraw or revoke an
 * invitation to at a row, under `capability` (`membership.write` or
 * `user.invite`): ranked the same as the caller's own role there or below it
 * (ADR-0515; ZENITH.md §6.2), as Principal.RequireRole decides on the server.
 * An owner may give `owner`; an admin may give any role but `owner`. The
 * superuser is not capped. The server is the authority; this only decides
 * which roles a form offers and where a Revoke button is drawn. */
export function grantableRoles(me: Me | undefined, capability: Capability, row: RowScope): Role[] {
  if (!me || !can(me, capability, row)) return [];
  const all = Object.keys(RANK) as Role[];
  if (me.user.superuser) return all;
  const own = roleAt(me, row);
  return own === undefined ? [] : all.filter((r) => RANK[r] >= RANK[own]);
}
